import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { PrimitiveTypes } from '@angular/cli/src/analytics/analytics-parameters';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgClass } from '@angular/common';
import { LoaderComponent } from '../../loaders/loader/loader.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

export enum IconPosition {
  Left = 'left'
}

export enum InputStyle {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary'
}

export enum InputSize {
  Medium = 'medium',
  Big = 'big'
}

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    },
    provideNgxMask()
  ],
  imports: [NgClass, LoaderComponent, NgxMaskDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputComponent implements ControlValueAccessor {
  label: InputSignal<string> = input<string>('');
  placeholder: InputSignal<string> = input<string>('');
  autocomplete: InputSignal<string | undefined> = input<string | undefined>(
    undefined
  );
  name: InputSignal<string | undefined> = input<string | undefined>(undefined);
  mask: InputSignal<string | undefined> = input<string | undefined>(undefined);
  showMaskTyped: InputSignal<boolean> = input<boolean>(false);
  hiddenInput: InputSignal<boolean> = input<boolean>(false);
  type: InputSignal<string> = input<string>('text');
  style: InputSignal<InputStyle> = input<InputStyle>(InputStyle.Primary);
  size: InputSignal<InputSize> = input<InputSize>(InputSize.Medium);
  outline: InputSignal<boolean> = input<boolean>(true);

  // Icon
  icon: InputSignal<string> = input<string>('');
  iconPosition: InputSignal<IconPosition> = input<IconPosition>(
    IconPosition.Left
  );

  // State
  value: WritableSignal<PrimitiveTypes> = signal<PrimitiveTypes>('');
  isDisabled: WritableSignal<boolean> = signal<boolean>(false);
  showPassword: WritableSignal<boolean> = signal(false);

  // Button
  buttonLabel: InputSignal<string> = input<string>('');
  buttonLoading: InputSignal<boolean> = input<boolean>(false);
  buttonDisabled: InputSignal<boolean> = input<boolean>(false);
  buttonClick: OutputEmitterRef<Event> = output<Event>();

  get inputType(): string {
    return this.type() === 'password' && this.showPassword()
      ? 'text'
      : this.type();
  }

  onValueChange(event: Event) {
    const eventTarget: HTMLTextAreaElement =
      event.target as HTMLTextAreaElement;

    this.value.set(eventTarget.value);
    this.onChange(eventTarget.value);
  }

  onButtonClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.buttonClick.emit(event);
  }

  onEnterKey(event: Event) {
    // If there's a button with this input, trigger it on Enter
    if (this.buttonLabel() && !this.isDisabled() && !this.buttonLoading() && !this.buttonDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      this.buttonClick.emit(event);
    }
  }

  togglePasswordVisibility() {
    this.showPassword.update((visible) => !visible);
  }

  // ControlValueAccessor
  private onChange = (value: PrimitiveTypes) => {};
  onTouched = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  writeValue(outsideValue: PrimitiveTypes): void {
    this.value.set(outsideValue);
  }
}
